import { Ticket, ticketService } from "@/services/ticketService";

// HTML tag'lerini temizle
const stripHtml = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>?/g, '')     // Tüm HTML tag'lerini temizle (yarım olanlar dahil)
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, MessageSquare, Building2, Clock, AlertCircle, CheckCircle, XCircle, Users, FileX, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TicketTableProps {
  tickets: Ticket[];
  onTicketDeleted?: () => void;
}

export function TicketTable({ tickets, onTicketDeleted }: TicketTableProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Sayfalama için state'ler
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Silme dialog state'leri
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Ticket silme
  const handleDelete = async () => {
    if (!ticketToDelete) return;
    setDeleting(true);
    try {
      await ticketService.deleteTicket(ticketToDelete._id);
      toast({
        title: 'Başarılı',
        description: `Ticket #${ticketToDelete.ticketNumber} silindi.`
      });
      onTicketDeleted?.();
    } catch (error) {
      console.error('Ticket silinemedi:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Ticket silinemedi.'
      });
    }
    setDeleting(false);
    setDeleteDialogOpen(false);
    setTicketToDelete(null);
  };

  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(tickets.length / itemsPerPage);

  // Mevcut sayfada gösterilecek ticketları belirle
  const currentTickets = tickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Sayfa değiştirme fonksiyonu
  const changePage = (page: number) => {
    setCurrentPage(page);
  };

  // Önceki ve sonraki sayfalara geçiş fonksiyonları
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Status Badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
            <AlertCircle className="h-3 w-3 mr-1 text-green-600" />
            Açık
          </div>
        );
      case 'in_progress':
        return (
          <div className="inline-flex items-center rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
            <Clock className="h-3 w-3 mr-1 text-blue-600" />
            İşlemde
          </div>
        );
      case 'waiting_customer':
        return (
          <div className="inline-flex items-center rounded-lg bg-yellow-100 px-2.5 py-1 text-xs font-semibold text-yellow-800">
            <Users className="h-3 w-3 mr-1 text-yellow-600" />
            Müşteri Bekliyor
          </div>
        );
      case 'resolved':
        return (
          <div className="inline-flex items-center rounded-lg bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-800">
            <CheckCircle className="h-3 w-3 mr-1 text-purple-600" />
            Çözüldü
          </div>
        );
      case 'closed':
        return (
          <div className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800">
            <XCircle className="h-3 w-3 mr-1 text-gray-600" />
            Kapatıldı
          </div>
        );
      default:
        return null;
    }
  };

  // Priority Badge
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'low':
        return (
          <div className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800">
            <div className="mr-1 h-1.5 w-1.5 rounded-full bg-gray-500"></div>
            Düşük
          </div>
        );
      case 'medium':
        return (
          <div className="inline-flex items-center rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
            <div className="mr-1 h-1.5 w-1.5 rounded-full bg-blue-500"></div>
            Orta
          </div>
        );
      case 'high':
        return (
          <div className="inline-flex items-center rounded-lg bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">
            <div className="mr-1 h-1.5 w-1.5 rounded-full bg-orange-500"></div>
            Yüksek
          </div>
        );
      case 'urgent':
        return (
          <div className="inline-flex items-center rounded-lg bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
            <div className="mr-1 h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"></div>
            Acil
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Ticket Tablosu */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
              <TableHead className="py-4 pl-6 text-slate-700 font-bold text-sm w-[80px]">Ticket No</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[320px]">Konu & Müşteri</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[200px]">Durum & Öncelik</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[180px]">Firma</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[140px]">Tarih</TableHead>
              <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[80px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-500">
                    <FileX className="h-10 w-10 mb-2 text-slate-400" />
                    <p>Ticket bulunamadı.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              currentTickets.map((ticket) => (
                <TableRow
                  key={ticket._id}
                  className="border-b border-slate-100 hover:bg-emerald-50/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/tickets/${ticket._id}`)}
                >
                  <TableCell className="py-4 pl-6">
                    <div className="flex items-center">
                      <span className="font-mono text-sm font-semibold text-slate-600">
                        #{ticket.ticketNumber}
                      </span>
                      {ticket.adminUnread && (
                        <div className="ml-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-900 break-words text-base line-clamp-1">
                        {ticket.subject}
                      </span>
                      <span className="text-sm text-slate-500 mt-0.5 line-clamp-1">
                        {stripHtml(ticket.lastMessagePreview || ticket.description)}
                      </span>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-slate-400">
                          {ticket.createdBy.name}
                        </span>
                        <span className="text-xs text-slate-300">•</span>
                        <span className="text-xs text-slate-400">
                          {ticket.createdBy.email}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex flex-wrap gap-2">
                      {getStatusBadge(ticket.status)}
                      {getPriorityBadge(ticket.priority)}
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 mr-2.5">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-slate-700 line-clamp-1">
                        {ticket.company?.name || "Bilinmeyen"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="space-y-1">
                      <div className="flex items-center text-xs text-slate-600">
                        <MessageSquare className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                        <span>{new Date(ticket.lastMessageAt).toLocaleDateString('tr-TR')}</span>
                      </div>
                      <div className="flex items-center text-xs text-slate-500">
                        <Clock className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                        <span>{new Date(ticket.lastMessageAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right pr-6" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100">
                          <span className="sr-only">İşlemler</span>
                          <MoreHorizontal className="h-5 w-5 text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => navigate(`/tickets/${ticket._id}`)}>
                          <MessageSquare className="h-4 w-4 mr-2 text-slate-500" />
                          <span>Detayları Görüntüle</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={() => {
                            setTicketToDelete(ticket);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          <span>Sil</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Sayfalama kontrolleri */}
      {tickets.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{tickets.length}</span> ticket içinden{" "}
            <span className="font-semibold text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span>-
            <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, tickets.length)}</span>{" "}
            arası gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Önceki sayfa"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Önceki</span>
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Sayfa düğmeleri için başlangıç sayfasını hesapla
                let startPage = Math.max(1, currentPage - 2);
                if (currentPage > totalPages - 2) {
                  startPage = Math.max(1, totalPages - 4);
                }

                const page = startPage + i;
                if (page <= totalPages) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => changePage(page)}
                      className={`h-10 min-w-10 px-3 rounded-lg ${
                        currentPage === page
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white pointer-events-none"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </Button>
                  );
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Sonraki sayfa"
            >
              <span className="hidden sm:inline">Sonraki</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ticket'ı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              {ticketToDelete && (
                <>
                  #{ticketToDelete.ticketNumber} numaralı ticket'ı silmek istediğinizden emin misiniz?
                  Bu işlem geri alınamaz ve tüm mesajlar silinecektir.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Siliniyor...' : 'Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
